import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { LegalLayout, type LegalSection } from "@/components/calv/LegalLayout";

const CREATED = "07.05.2026";
const UPDATED = "07.05.2026";
const NEXT_REVIEW = "07.11.2026";

const sections: LegalSection[] = [
  {
    id: "hero-intro",
    num: "01",
    title: "Barrierefreiheit ist nicht abgehakt, sondern laufend gepflegt",
    content: (
      <>
        <p>
          Diese Erklärung dokumentiert ehrlich, wie barrierefrei calvarius.com aktuell ist —
          was funktioniert, was eingeschränkt funktioniert, und wo wir nachbessern.
          Selbstbewertung nach WCAG 2.1 Level AA, gemäß Barrierefreiheitsstärkungsgesetz (BFSG).
        </p>
        <p>
          <strong>Stand:</strong> {CREATED} · <strong>Norm:</strong> WCAG 2.1 Level AA ·{" "}
          <strong>Methode:</strong> Selbstbewertung
        </p>
      </>
    ),
  },
  {
    id: "standpunkt",
    num: "02",
    title: "Was Barrierefreiheit für uns konkret bedeutet",
    content: (
      <>
        <p>
          Calvarius betreibt diese Website in der Überzeugung, dass digitale Substanz auch
          zugänglich sein muss. Nicht weil das Gesetz es ab Juni 2025 verlangt, sondern weil
          eine Site, die Teile ihrer Besucher ausschließt, ihren eigenen wirtschaftlichen
          Anspruch unterläuft. Wer sagt „wir steigern Online-Performance", aber Menschen mit
          Sehbehinderung, motorischer Einschränkung oder kognitiven Besonderheiten von der
          eigenen Site fernhält, hat ein Glaubwürdigkeits-Problem.
        </p>
        <p>
          Wir setzen bewusst <strong>keine Overlay-Tools</strong> ein. Diese Tools werden von der Mehrheit der Menschen mit
          Behinderungen, von Accessibility-Experten und von Behindertenverbänden nicht
          empfohlen, weil sie die Probleme im Code nicht lösen, sondern überlagern — und in
          vielen Fällen die Nutzung mit echten Hilfstechnologien wie Screenreadern (NVDA,
          JAWS, VoiceOver) sogar erschweren. Stattdessen bauen wir Barrierefreiheit
          strukturell ein, an der richtigen Stelle: im Code, im Design, in der
          Heading-Hierarchie, in Tastatur-Navigation, in Kontrasten, in Alternativtexten.
        </p>
        <p>
          Diese Erklärung ist Selbstbewertung — wir prüfen die Site selbst, dokumentieren
          ehrlich, wo Lücken bestehen, und arbeiten an Verbesserungen. Eine externe
          Bestätigung durch einen zertifizierten Auditor (BIK BITV oder vergleichbar) ist für
          später geplant. Bis dahin gilt: was hier steht, ist ehrlich erhoben — wenn etwas
          nicht funktioniert, sagen wir das.
        </p>
      </>
    ),
  },
  {
    id: "stand",
    num: "03",
    title: "Stand der Vereinbarkeit",
    content: (
      <>
        <p>
          Die Website calvarius.com ist nach Selbstbewertung des Betreibers{" "}
          <strong>teilweise vereinbar</strong> mit den Anforderungen der
          Barrierefreien-Informationstechnik-Verordnung (BITV 2.0) und WCAG 2.1 Level AA.
        </p>
        <p>
          Die Bewertung „teilweise vereinbar" wurde gewählt, weil zwar die überwiegende
          Mehrheit der WCAG-Erfolgskriterien erfüllt ist, aber einzelne Bereiche noch
          Verbesserungsbedarf zeigen. Die konkreten Einschränkungen sind in Sektion 04
          dokumentiert.
        </p>
        <p>Konkret heißt das:</p>
        <ul>
          <li>Strukturelle Code-Basis (Heading-Hierarchie, Semantik, ARIA-Rollen) ist konform</li>
          <li>Tastatur-Navigation funktioniert auf allen Routen</li>
          <li>Farbkontraste sind nach WCAG-AA-Anforderung geprüft</li>
          <li>Alle Bilder haben Alternativtexte</li>
          <li>Formulare haben Labels und Fehlermeldungen</li>
          <li>Die Site ist mit Screenreadern (NVDA, VoiceOver) bedienbar</li>
          <li>Skalierung bis 200 % funktioniert ohne Layout-Brüche</li>
        </ul>
      </>
    ),
  },
  {
    id: "einschraenkungen",
    num: "04",
    title: "Bekannte Einschränkungen",
    content: (
      <>
        <p>
          <strong>Eingeschränkt zugänglich:</strong>
        </p>
        <ul>
          <li>
            <strong>Animationen auf der Startseite (Hero-Bereich)</strong> — Einzelne
            Animationen faden die Hauptüberschrift ein. Personen mit der Browser-Einstellung
            „prefers-reduced-motion" sollten Animationen automatisch reduziert sehen, dies ist
            bisher nicht durchgängig getestet. WCAG-Bezug: Erfolgskriterium 2.3.3 (Animation
            aus Interaktionen).
          </li>
          <li>
            <strong>Einbettungen externer Inhalte</strong> — Sofern wir externe Tools (z. B.
            Karten, Video-Player) einbinden, können wir deren Barrierefreiheit nicht
            garantieren. Aktuell sind keine externen Einbettungen aktiv. Sollte sich das
            ändern, wird die Liste hier ergänzt.
          </li>
          <li>
            <strong>PDF-Dokumente (sofern vorhanden)</strong> — Falls Calvarius zukünftig
            PDF-Dokumente zum Download anbietet, sind diese eigenständig auf Barrierefreiheit
            zu prüfen. Aktuell sind keine PDF-Dokumente eingebunden.
          </li>
        </ul>
        <p>
          <strong>Geprüft und für vereinbar befunden:</strong>
        </p>
        <ul>
          <li>Die gesamte Navigation (Header, Footer, Mega-Menü)</li>
          <li>Alle Service-Detailseiten (Performance-Marketing, SEO, Tracking, etc.)</li>
          <li>Alle Performance-Marketing-Sub-Seiten (Google Ads, Meta Ads, etc.)</li>
          <li>Die Tool-Detailseiten (Delta, picazzoo, GonzalAds)</li>
          <li>Das Kontaktformular</li>
          <li>Die rechtlichen Seiten (Impressum, Datenschutz)</li>
        </ul>
      </>
    ),
  },
  {
    id: "bericht",
    num: "05",
    title: "Erstellung des Berichts",
    content: (
      <>
        <p>
          <strong>Erstellungsdatum:</strong> {CREATED}
        </p>
        <p>
          <strong>Geprüfte Version:</strong> Stand der Live-Version am Erstellungsdatum.
        </p>
        <p>
          <strong>Methode:</strong> Selbstbewertung durch den Betreiber Calvarius. Verwendete
          Werkzeuge:
        </p>
        <ul>
          <li>Manuelle Prüfung mit Tastatur-Navigation (kein Maus-Einsatz)</li>
          <li>Screenreader-Test mit NVDA (Windows) und VoiceOver (macOS)</li>
          <li>Automatisierte Prüfung mit axe DevTools (Chrome-Extension)</li>
          <li>Lighthouse Accessibility-Audit (Chrome DevTools)</li>
          <li>WAVE Web Accessibility Evaluation Tool</li>
          <li>Manuelle Kontrastprüfung mit WebAIM Contrast Checker</li>
          <li>Skalierungstest bis 200 % in Chrome, Firefox, Safari</li>
        </ul>
        <p>
          <strong>Geprüfte Erfolgskriterien:</strong> Alle 50 Kriterien der WCAG 2.1 Level A
          und Level AA.
        </p>
        <p>
          <strong>Was nicht geprüft wurde:</strong> Externe Tools (sofern später eingebunden),
          PDF-Dokumente (aktuell keine vorhanden), Drittanbieter-Skripte (aktuell keine
          eingebunden außer notwendige Web-Analyse).
        </p>
        <p>
          <strong>Externe Bestätigung:</strong> Bisher keine. Eine externe Prüfung durch eine
          zertifizierte Stelle (z. B. BIK BITV) ist für Q4 2026 geplant.
        </p>
      </>
    ),
  },
  {
    id: "feedback",
    num: "06",
    title: "Feedback-Mechanismus",
    content: (
      <>
        <p>
          Sollten Sie Inhalte oder Funktionen auf calvarius.com vorfinden, die für Sie nicht
          zugänglich sind, freuen wir uns über Ihre Rückmeldung. Wir nehmen jede Meldung ernst
          und antworten in der Regel innerhalb von fünf Werktagen.
        </p>
        <p>
          <strong>Kontakt für Barrierefreiheits-Anliegen:</strong>
        </p>
        <p>
          E-Mail: <a href="mailto:info@calvarius.com">info@calvarius.com</a>
          <br />
          Betreff bitte mit „Barrierefreiheit:" beginnen, damit Ihre Anfrage direkt an die
          zuständige Person geht.
        </p>
        <p>Bitte geben Sie in Ihrer Meldung an:</p>
        <ul>
          <li>Welche Seite betroffen ist (URL oder Beschreibung)</li>
          <li>Welche Hilfstechnologie Sie verwenden (sofern relevant)</li>
          <li>Welche Barriere konkret aufgetreten ist</li>
          <li>Falls möglich: Browser und Betriebssystem</li>
        </ul>
        <p>
          Wir verwenden Ihre Angaben ausschließlich zur Behebung der gemeldeten Barriere und
          kommen mit Ihrer Erlaubnis ggf. mit Rückfragen auf Sie zu.
        </p>
      </>
    ),
  },
  {
    id: "schlichtung",
    num: "07",
    title: "Schlichtungsverfahren",
    content: (
      <>
        <p>
          Sollten Sie mit unserer Antwort nicht zufrieden sein oder sollten wir nicht
          innerhalb von vier Wochen reagieren, können Sie das Schlichtungsverfahren nach § 16
          Behindertengleichstellungsgesetz (BGG) bei der Schlichtungsstelle BGG einleiten. Das
          Verfahren ist für Sie kostenfrei und es ist keine anwaltliche Vertretung
          erforderlich.
        </p>
        <p>
          <strong>Kontakt der Schlichtungsstelle BGG:</strong>
        </p>
        <p>
          Schlichtungsstelle nach dem Behindertengleichstellungsgesetz
          <br />
          bei dem Beauftragten der Bundesregierung für die Belange von Menschen mit
          Behinderungen
          <br />
          Mauerstraße 53
          <br />
          10117 Berlin
          <br />
          Telefon: 030 18 527-2805
          <br />
          E-Mail:{" "}
          <a href="mailto:info@schlichtungsstelle-bgg.de">info@schlichtungsstelle-bgg.de</a>
          <br />
          Website:{" "}
          <a href="https://www.schlichtungsstelle-bgg.de" target="_blank" rel="noreferrer">
            www.schlichtungsstelle-bgg.de
          </a>
        </p>
      </>
    ),
  },
  {
    id: "stand-erklaerung",
    num: "08",
    title: "Letzte Aktualisierung",
    content: (
      <>
        <p>
          <strong>Erstellt am:</strong> {CREATED}
          <br />
          <strong>Letzte Aktualisierung:</strong> {UPDATED}
          <br />
          <strong>Nächste geplante Überprüfung:</strong> {NEXT_REVIEW}
        </p>
        <p>
          Diese Erklärung wird mindestens einmal jährlich überprüft und nach jeder
          substanziellen Änderung der Website neu bewertet.
        </p>
      </>
    ),
  },
];

export const Route = createFileRoute("/barrierefreiheit")({
  head: () => {
    const h = pageHead("accessibility", "de");
    return { meta: h.meta, links: h.links };
  },
  component: () => (
    <LegalLayout
      eyebrow="BF."
      title="Erklärung zur Barrierefreiheit"
      meta={`Stand: ${CREATED} · WCAG 2.1 Level AA · Selbstbewertung`}
      crumbLabel="Barrierefreiheit"
      sections={sections}
    />
  ),
});
